import React, { useState, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { User, Car, Heart, Shield, Camera, Upload, Check } from 'lucide-react';
import './ProfileSetup.css';

export const ProfileSetup = () => {
  const { user, updateProfile, uploadProfilePhoto } = useAuth();
  const navigate = useNavigate();
  const [currentStep, setCurrentStep] = useState(1);
  const photoInputRef = useRef(null);
  const vehiclePhotoRef = useRef(null);
  const idDocumentRef = useRef(null);

  const [formData, setFormData] = useState({
    // Persönliche Daten
    firstName: user?.firstName || '',
    lastName: user?.lastName || '',
    dateOfBirth: user?.dateOfBirth || '',
    phone: user?.phone || '',
    profilePhoto: user?.profilePhoto || null,
    bio: user?.bio || '',

    // Fahrzeug (optional)
    vehicleMake: '',
    vehicleModel: '',
    vehicleColor: '',
    vehiclePlate: '',
    vehiclePhoto: null,

    // Präferenzen
    smoking: 'non-smoker', // non-smoker, allowed
    pets: 'none', // none, welcome
    music: 'yes', // yes, quiet

    // Verifizierung
    idDocument: null,
    idDocumentType: 'passport' // passport, id-card
  });

  const [uploading, setUploading] = useState(false);
  const [message, setMessage] = useState(null);

  const steps = [
    { id: 1, title: 'Persönliche Daten', icon: User },
    { id: 2, title: 'Fahrzeug', icon: Car },
    { id: 3, title: 'Präferenzen', icon: Heart },
    { id: 4, title: 'Verifizierung', icon: Shield }
  ];

  const handlePhotoUpload = async (e, type) => {
    const file = e.target.files[0];
    if (!file) return;

    if (file.size > 5 * 1024 * 1024) {
      setMessage({ type: 'error', text: 'Datei zu groß (max 5MB)' });
      return;
    }

    setUploading(true);

    if (type === 'profile') {
      const result = await uploadProfilePhoto(file);
      if (result.success) {
        setFormData({ ...formData, profilePhoto: result.photoUrl });
        setMessage({ type: 'success', text: 'Foto hochgeladen' });
      } else {
        setMessage({ type: 'error', text: result.error });
      }
    } else {
      // Store file for later upload
      const reader = new FileReader();
      reader.onloadend = () => {
        setFormData({ ...formData, [type]: reader.result });
      };
      reader.readAsDataURL(file);
    }

    setUploading(false);
    setTimeout(() => setMessage(null), 3000);
  };

  const handleNext = () => {
    if (currentStep < 4) {
      setCurrentStep(currentStep + 1);
    }
  };

  const handleBack = () => {
    if (currentStep > 1) {
      setCurrentStep(currentStep - 1);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setUploading(true);

    const result = await updateProfile(formData);

    if (result.success) {
      setMessage({ type: 'success', text: 'Profil erstellt!' });
      setTimeout(() => {
        navigate('/profile');
      }, 2000);
    } else {
      setMessage({ type: 'error', text: result.error });
    }

    setUploading(false);
  };

  return (
    <div className="profile-setup-container">
      <div className="setup-header">
        <h1>Profil erstellen</h1>
        <p>Vervollständige deine Daten, um Fahrten anzubieten oder zu buchen.</p>
        <div className="step-indicator">
          Schritt {currentStep} von 4
        </div>
      </div>

      <div className="setup-content">
        {/* Sidebar Navigation */}
        <aside className="setup-sidebar">
          <nav className="setup-nav">
            {steps.map((step) => {
              const Icon = step.icon;
              return (
                <button
                  key={step.id}
                  onClick={() => setCurrentStep(step.id)}
                  className={`nav-item ${currentStep === step.id ? 'active' : ''} ${currentStep > step.id ? 'completed' : ''}`}
                >
                  <Icon size={20} />
                  <span>{step.title}</span>
                  {currentStep > step.id && <Check size={16} className="check-icon" />}
                </button>
              );
            })}
          </nav>
        </aside>

        {/* Main Form */}
        <main className="setup-main">
          {message && (
            <div className={`message-box ${message.type}`}>
              {message.text}
            </div>
          )}

          <form onSubmit={handleSubmit}>
            {/* STEP 1: Persönliche Daten */}
            {currentStep === 1 && (
              <div className="form-section">
                <h2>Persönliche Daten</h2>

                {/* Foto hochladen */}
                <div className="photo-upload-section">
                  <div className="photo-preview">
                    {formData.profilePhoto ? (
                      <img src={formData.profilePhoto} alt="Profil" />
                    ) : (
                      <User size={64} />
                    )}
                  </div>
                  <div className="photo-info">
                    <h3>Foto hochladen</h3>
                    <p>Empfohlen: Quadratisch, mind. 400×400px</p>
                    <button
                      type="button"
                      onClick={() => photoInputRef.current?.click()}
                      className="upload-button"
                      disabled={uploading}
                    >
                      <Camera size={16} />
                      {uploading ? 'Hochladen...' : 'Foto auswählen'}
                    </button>
                    <input
                      ref={photoInputRef}
                      type="file"
                      accept="image/*"
                      onChange={(e) => handlePhotoUpload(e, 'profile')}
                      style={{ display: 'none' }}
                    />
                  </div>
                </div>

                {/* Name */}
                <div className="form-row">
                  <div className="form-group">
                    <label>Vorname</label>
                    <input
                      type="text"
                      value={formData.firstName}
                      onChange={(e) => setFormData({ ...formData, firstName: e.target.value })}
                      placeholder="Thomas"
                      required
                    />
                  </div>
                  <div className="form-group">
                    <label>Nachname</label>
                    <input
                      type="text"
                      value={formData.lastName}
                      onChange={(e) => setFormData({ ...formData, lastName: e.target.value })}
                      placeholder="Dein Nachname"
                      required
                    />
                  </div>
                </div>

                {/* Geburtsdatum & Telefon */}
                <div className="form-row">
                  <div className="form-group">
                    <label>Geburtsdatum</label>
                    <input
                      type="date"
                      value={formData.dateOfBirth}
                      onChange={(e) => setFormData({ ...formData, dateOfBirth: e.target.value })}
                      required
                    />
                  </div>
                  <div className="form-group">
                    <label>Telefonnummer</label>
                    <input
                      type="tel"
                      value={formData.phone}
                      onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                      placeholder="+49 123 4567890"
                      required
                    />
                  </div>
                </div>

                {/* Über mich */}
                <div className="form-group">
                  <label>Über mich</label>
                  <textarea
                    value={formData.bio}
                    onChange={(e) => setFormData({ ...formData, bio: e.target.value })}
                    placeholder="Erzähle etwas über dich, z.B. warum du reist oder was du gerne hörst."
                    rows={4}
                  />
                  <small>Dieser Text wird auf deinem öffentlichen Profil angezeigt.</small>
                </div>
              </div>
            )}

            {/* STEP 2: Fahrzeug */}
            {currentStep === 2 && (
              <div className="form-section">
                <h2>Fahrzeug <span className="optional">(Optional für Mitfahrer)</span></h2>

                <div className="form-row">
                  <div className="form-group">
                    <label>Marke</label>
                    <input
                      type="text"
                      value={formData.vehicleMake}
                      onChange={(e) => setFormData({ ...formData, vehicleMake: e.target.value })}
                      placeholder="z.B. Volkswagen"
                    />
                  </div>
                  <div className="form-group">
                    <label>Modell</label>
                    <input
                      type="text"
                      value={formData.vehicleModel}
                      onChange={(e) => setFormData({ ...formData, vehicleModel: e.target.value })}
                      placeholder="z.B. Golf VII"
                    />
                  </div>
                </div>

                <div className="form-row">
                  <div className="form-group">
                    <label>Farbe</label>
                    <input
                      type="text"
                      value={formData.vehicleColor}
                      onChange={(e) => setFormData({ ...formData, vehicleColor: e.target.value })}
                      placeholder="z.B. Grau"
                    />
                  </div>
                  <div className="form-group">
                    <label>Kennzeichen</label>
                    <input
                      type="text"
                      value={formData.vehiclePlate}
                      onChange={(e) => setFormData({ ...formData, vehiclePlate: e.target.value })}
                      placeholder="B - XY 123"
                    />
                    <small>Wird nur gebuchten Mitfahrern angezeigt.</small>
                  </div>
                </div>

                {/* Fahrzeugfoto */}
                <div className="form-group">
                  <label>Fahrzeugfoto</label>
                  <div className="file-upload-box">
                    {formData.vehiclePhoto ? (
                      <div className="preview-image">
                        <img src={formData.vehiclePhoto} alt="Fahrzeug" />
                        <button
                          type="button"
                          onClick={() => setFormData({ ...formData, vehiclePhoto: null })}
                          className="remove-button"
                        >
                          Entfernen
                        </button>
                      </div>
                    ) : (
                      <>
                        <Upload size={32} />
                        <p>Klicken zum Hochladen oder hierher ziehen</p>
                        <small>JPG, PNG bis 5MB</small>
                        <button
                          type="button"
                          onClick={() => vehiclePhotoRef.current?.click()}
                          className="upload-button"
                        >
                          Datei auswählen
                        </button>
                      </>
                    )}
                    <input
                      ref={vehiclePhotoRef}
                      type="file"
                      accept="image/*"
                      onChange={(e) => handlePhotoUpload(e, 'vehiclePhoto')}
                      style={{ display: 'none' }}
                    />
                  </div>
                </div>
              </div>
            )}

            {/* STEP 3: Reise-Präferenzen */}
            {currentStep === 3 && (
              <div className="form-section">
                <h2>Reise-Präferenzen</h2>

                {/* Rauchen */}
                <div className="preference-group">
                  <label>Rauchen im Auto</label>
                  <div className="radio-group">
                    <label className="radio-option">
                      <input
                        type="radio"
                        name="smoking"
                        value="non-smoker"
                        checked={formData.smoking === 'non-smoker'}
                        onChange={(e) => setFormData({ ...formData, smoking: e.target.value })}
                      />
                      <span>Nichtraucher-Fahrzeug</span>
                    </label>
                    <label className="radio-option">
                      <input
                        type="radio"
                        name="smoking"
                        value="allowed"
                        checked={formData.smoking === 'allowed'}
                        onChange={(e) => setFormData({ ...formData, smoking: e.target.value })}
                      />
                      <span>Rauchen erlaubt</span>
                    </label>
                  </div>
                </div>

                {/* Tiere */}
                <div className="preference-group">
                  <label>Tiere</label>
                  <div className="radio-group">
                    <label className="radio-option">
                      <input
                        type="radio"
                        name="pets"
                        value="none"
                        checked={formData.pets === 'none'}
                        onChange={(e) => setFormData({ ...formData, pets: e.target.value })}
                      />
                      <span>Keine Tiere</span>
                    </label>
                    <label className="radio-option">
                      <input
                        type="radio"
                        name="pets"
                        value="welcome"
                        checked={formData.pets === 'welcome'}
                        onChange={(e) => setFormData({ ...formData, pets: e.target.value })}
                      />
                      <span>Tiere willkommen</span>
                    </label>
                  </div>
                </div>

                {/* Musik */}
                <div className="preference-group">
                  <label>Musik</label>
                  <div className="radio-group">
                    <label className="radio-option">
                      <input
                        type="radio"
                        name="music"
                        value="yes"
                        checked={formData.music === 'yes'}
                        onChange={(e) => setFormData({ ...formData, music: e.target.value })}
                      />
                      <span>Gerne Musik</span>
                    </label>
                    <label className="radio-option">
                      <input
                        type="radio"
                        name="music"
                        value="quiet"
                        checked={formData.music === 'quiet'}
                        onChange={(e) => setFormData({ ...formData, music: e.target.value })}
                      />
                      <span>Lieber Stille</span>
                    </label>
                  </div>
                </div>
              </div>
            )}

            {/* STEP 4: Verifizierung */}
            {currentStep === 4 && (
              <div className="form-section">
                <h2>Verifizierung</h2>
                <p className="verification-info">
                  Um das Vertrauen in der Community zu stärken, bitten wir dich, deinen Ausweis hochzuladen. 
                  Die Daten werden verschlüsselt und nicht veröffentlicht.
                </p>

                <div className="form-group">
                  <label>Personalausweis oder Reisepass</label>
                  <small>Vorder- und Rückseite erforderlich</small>
                  
                  <div className="file-upload-box">
                    {formData.idDocument ? (
                      <div className="preview-document">
                        <Shield size={48} />
                        <p>Dokument hochgeladen</p>
                        <button
                          type="button"
                          onClick={() => setFormData({ ...formData, idDocument: null })}
                          className="remove-button"
                        >
                          Entfernen
                        </button>
                      </div>
                    ) : (
                      <>
                        <Shield size={32} />
                        <p>Personalausweis oder Reisepass hochladen</p>
                        <button
                          type="button"
                          onClick={() => idDocumentRef.current?.click()}
                          className="upload-button"
                        >
                          Datei auswählen
                        </button>
                      </>
                    )}
                    <input
                      ref={idDocumentRef}
                      type="file"
                      accept="image/*,.pdf"
                      onChange={(e) => handlePhotoUpload(e, 'idDocument')}
                      style={{ display: 'none' }}
                    />
                  </div>
                </div>
              </div>
            )}

            {/* Navigation Buttons */}
            <div className="form-actions">
              {currentStep > 1 && (
                <button
                  type="button"
                  onClick={handleBack}
                  className="back-button"
                >
                  Zurück
                </button>
              )}

              {currentStep < 4 ? (
                <button
                  type="button"
                  onClick={handleNext}
                  className="next-button"
                >
                  Weiter
                </button>
              ) : (
                <button
                  type="submit"
                  className="submit-button"
                  disabled={uploading}
                >
                  {uploading ? 'Speichere...' : 'Profil speichern'}
                </button>
              )}
            </div>
          </form>
        </main>
      </div>
    </div>
  );
};